import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createStockCount, listStockCountLines, listStockCounts, postStockCount } from '../../api/inventoryOps';
import { listItems, listUoms } from '../../api/masterData';
import { listWarehouses } from '../../api/orgData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { DocumentStatus, Item, StockCount, StockCountLine, Uom, Warehouse } from '../../types/domain';

const OPNAME_STATUSES: DocumentStatus[] = ['DRAFT', 'POSTED', 'CANCELLED'];

/**
 * Stock Opname (Stock Count) — create a DRAFT physical-count session
 * (fn_create_stock_count) then post it (fn_post_stock_count_checked),
 * which writes ADJUSTMENT_IN/OUT to stock_ledger for every line whose
 * counted_qty differs from the system_qty snapshotted at creation time.
 * Migration 0033, permission-gated + branch-scoped — same pattern as
 * every other FASE 6/VS1 write.
 */
export default function StockOpnamePage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [counts, setCounts] = useState<StockCount[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const [selectedCount, setSelectedCount] = useState<StockCount | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const canCreate = hasPermission('stock_count', 'create');
  const canPost = hasPermission('stock_count', 'post');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [countRows, itemRows, uomRows, whRows] = await Promise.all([
        listStockCounts(activeBranchId ?? undefined),
        listItems(),
        listUoms(),
        listWarehouses(activeBranchId ?? undefined),
      ]);
      setCounts(countRows);
      setItems(itemRows);
      setUoms(uomRows);
      setWarehouses(whRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const handlePost = async (countId: string) => {
    setActionError(null);
    setPostingId(countId);
    try {
      await postStockCount(countId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setPostingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return counts.filter((c) => {
      if (statusFilter !== 'ALL' && c.status !== statusFilter) return false;
      if (q && !c.count_number.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [counts, search, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);
  const opnameCounts = statusCounts(counts, OPNAME_STATUSES);

  return (
    <div>
      <PageHeader title="Stock Opname" subtitle="Sesi hitung fisik — posting menulis ADJUSTMENT_IN/OUT ke Stock Ledger sesuai selisih. Migration 0010/0033." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId || warehouses.length === 0}>
          {showForm ? 'Batal' : '+ Sesi opname baru'}
        </Button>
      )}
      {canCreate && activeBranchId && warehouses.length === 0 && !loading && <ErrorNote text="Tidak ada gudang aktif di branch ini." />}
      {showForm && canCreate && activeBranchId && (
        <NewStockCountForm
          items={items}
          uoms={uoms}
          warehouses={warehouses}
          branchId={activeBranchId}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {actionError && <ErrorNote text={actionError} />}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && counts.length > 0 && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Sesi', value: counts.length },
              { label: 'Draft', value: opnameCounts.DRAFT, tone: opnameCounts.DRAFT > 0 ? 'warning' : 'success' },
              { label: 'Posted', value: opnameCounts.POSTED, tone: 'success' },
              { label: 'Cancelled', value: opnameCounts.CANCELLED, tone: 'neutral' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari no. opname..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="DRAFT">Draft</option>
                <option value="POSTED">Posted</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            }
          />
        </>
      )}
      {!loading && !error && counts.length === 0 && <EmptyState text="Belum ada sesi stock opname." />}
      {!loading && !error && counts.length > 0 && total === 0 && <EmptyState text="Tidak ada sesi yang cocok dengan pencarian/filter." />}
      {total > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>No. Opname</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Diposting</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((c) => (
              <ClickableRow key={c.count_id} onClick={() => setSelectedCount(c)}>
                <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{c.count_number}</td>
                <td style={tdStyle}>
                  <StatusBadge status={c.status} />
                </td>
                <td style={tdStyle}>{formatDateTime(c.posted_at)}</td>
                <td style={tdStyle}>
                  {canPost && c.status === 'DRAFT' && (
                    <Button
                      variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handlePost(c.count_id);
                      }}
                      loading={postingId === c.count_id}
                    >
                      Post ke Stock Ledger
                    </Button>
                  )}
                </td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}
      <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />

      <StockCountDetailDrawer count={selectedCount} items={items} uoms={uoms} warehouses={warehouses} onClose={() => setSelectedCount(null)} />
    </div>
  );
}

function StockCountDetailDrawer({
  count,
  items,
  uoms,
  warehouses,
  onClose,
}: {
  count: StockCount | null;
  items: Item[];
  uoms: Uom[];
  warehouses: Warehouse[];
  onClose: () => void;
}) {
  const [lines, setLines] = useState<StockCountLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!count) return;
    setLines(null);
    setError(null);
    listStockCountLines(count.count_id)
      .then(setLines)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [count]);

  const itemLabel = (id: string) => {
    const it = items.find((i) => i.item_id === id);
    return it ? `${it.item_code} — ${it.name}` : id;
  };
  const uomLabel = (id: string) => uoms.find((u) => u.uom_id === id)?.code ?? id;
  const warehouseName = (id: string) => warehouses.find((w) => w.warehouse_id === id)?.name ?? id;

  return (
    <Drawer open={!!count} onClose={onClose} title={count ? `Opname ${count.count_number}` : 'Detail Opname'}>
      {count && (
        <div>
          <DrawerField label="No. Opname" value={count.count_number} />
          <DrawerField label="Status" value={<StatusBadge status={count.status} />} />
          <DrawerField label="Gudang" value={warehouseName(count.warehouse_id)} />
          <DrawerField label="Diposting" value={count.posted_at ? formatDateTime(count.posted_at) : 'Belum diposting'} />
          <DrawerField label="Dibuat" value={formatDateTime(count.created_at)} />

          <div style={{ marginTop: 16, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Baris Hitung</div>
          {error && <ErrorNote text={error} />}
          {!error && lines === null && <LoadingNote text="Memuat baris opname..." />}
          {lines && lines.length === 0 && <EmptyState text="Tidak ada baris pada sesi ini." />}
          {lines && lines.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Sistem</th>
                  <th style={thStyle}>Dihitung</th>
                  <th style={thStyle}>Selisih</th>
                  <th style={thStyle}>UOM</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.count_line_id}>
                    <td style={tdStyle}>{itemLabel(l.item_id)}</td>
                    <td style={tdStyle}>{l.system_qty}</td>
                    <td style={tdStyle}>{l.counted_qty}</td>
                    <td style={{ ...tdStyle, color: l.variance_qty === 0 ? 'var(--alex-text-primary)' : l.variance_qty > 0 ? 'var(--alex-success)' : 'var(--alex-danger)', fontWeight: 600 }}>
                      {l.variance_qty > 0 ? '+' : ''}
                      {l.variance_qty}
                    </td>
                    <td style={tdStyle}>{uomLabel(l.uom_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Drawer>
  );
}

interface DraftLine {
  itemId: string;
  uomId: string;
  countedQty: string;
}

function NewStockCountForm({
  items,
  uoms,
  warehouses,
  branchId,
  onCreated,
}: {
  items: Item[];
  uoms: Uom[];
  warehouses: Warehouse[];
  branchId: string;
  onCreated: () => void;
}) {
  const { context } = usePermissions();
  const [warehouseId, setWarehouseId] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ itemId: '', uomId: '', countedQty: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { itemId: '', uomId: '', countedQty: '' }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!context || !context.authenticated) return;
    setSubmitting(true);
    setError(null);
    try {
      await createStockCount({
        tenantId: context.tenant_id,
        branchId,
        warehouseId,
        lines: lines
          .filter((l) => l.itemId && l.uomId && l.countedQty !== '')
          .map((l) => ({ item_id: l.itemId, uom_id: l.uomId, counted_qty: Number(l.countedQty) })),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 220 }}>
          <FieldLabel label="Gudang">
            <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 10 }}>
          <div style={{ flex: 2 }}>
            <FieldLabel label="Item">
              <Select value={line.itemId} onChange={(e) => updateLine(i, { itemId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {items.map((it) => (
                  <option key={it.item_id} value={it.item_id}>
                    {it.item_code} — {it.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 100 }}>
            <FieldLabel label="UOM">
              <Select value={line.uomId} onChange={(e) => updateLine(i, { uomId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {uoms.map((u) => (
                  <option key={u.uom_id} value={u.uom_id}>
                    {u.code}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 120 }}>
            <FieldLabel label="Qty dihitung">
              <TextInput type="number" min="0" step="0.01" value={line.countedQty} onChange={(e) => updateLine(i, { countedQty: e.target.value })} required />
            </FieldLabel>
          </div>
          {lines.length > 1 && (
            <Button type="button" variant="ghost" onClick={() => removeLine(i)}>
              Hapus
            </Button>
          )}
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={addLine} style={{ marginBottom: 12 }}>
        + Baris
      </Button>
      <div>
        <Button type="submit" variant="primary" loading={submitting} disabled={!warehouseId}>
          Buat Sesi Opname (DRAFT)
        </Button>
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
